import './NewsDetail.scss'

//@ts-ignore
import Layout from '@componentsfld/core/Layout'
import { HeadProps } from 'gatsby'
import React, { FunctionComponent } from 'react'
import PuxRepeater from 'Root/Shared/components/PuxRepeater/PuxRepeater'
import { RepeaterContentType } from 'Root/Shared/queries/repeater-queries'
import {
  INewsAuthorItem,
  IPageContext,
} from 'Root/Shared/utils/data/buildTypedPage'
import { getLocalizedUrl } from 'Root/Shared/utils/localeURL'
import PuxLink from 'Root/Shared/utils/PuxLink'
import WidgetBuilder from 'Shared/components/builders/WidgetBuilder'
import PuxMediaSelector from 'Shared/components/content/mediaImage/PuxMediaSelector'
import EasySoftwareBreadcrumbs from 'Shared/components/EasySoftwareBreadcrumbs/EasySoftwareBreadcrumbs'
import EasySoftwareEditorContainer from 'Shared/components/EasySoftwareEditorContainer/EasySoftwareEditorContainer'
import PuxContent from 'Shared/components/PuxContent/PuxContent'
import PuxIcon from 'Shared/components/PuxIcon/PuxIcon'
import PuxVideo from 'Shared/components/PuxVideo/PuxVideo'
import {
  GenericPageProps,
  IEasySoftwareNewsPage,
} from 'Shared/queries/page-queries'
import getReadingDuration from 'Shared/utils/getReadingDuration'
import PuxMetaTagsGatsby from 'Shared/utils/PuxMetaTagsGatsby'
import { PuxVWO } from 'Root/Shared/utils/PuxVWO'
import { msgid, ngettext, t } from 'ttag'

function getNewsJson(
  item: IEasySoftwareNewsPage,
  type: string,
  author?: INewsAuthorItem
) {
  let json = {}

  const prefixUrl = process.env.GATSBY_WEBSITE_URL ?? ``

  if (author) {
    json = {
      '@context': `https://schema.org`,
      '@type': type,
      headline: item.displayText,
      author: {
        '@type': `Person`,
        name: author.name,
        url: [prefixUrl, author.url].join(`/`),
      },
      image: [
        item.newsImage?.mediaSelectorImage.resizePaths[0] ?? ``,
        item.newsImage?.mediaSelectorImage.resizePathsTablet[0] ?? ``,
        item.newsImage?.mediaSelectorImage.resizePathsMobile[0] ?? ``,
      ],
      datePublished: item.createdUtc,
      dateModified: item.modifiedUtc ?? ``,
    }
  } else {
    json = {
      '@context': `https://schema.org`,
      '@type': type,
      headline: item.displayText,
      author: {
        '@type': `Organization`,
        name: `EasySoftware`,
        url: prefixUrl,
      },
      image: [
        item.newsImage?.mediaSelectorImage.resizePaths[0] ?? ``,
        item.newsImage?.mediaSelectorImage.resizePathsTablet[0] ?? ``,
        item.newsImage?.mediaSelectorImage.resizePathsMobile[0] ?? ``,
      ],
      datePublished: item.createdUtc,
      dateModified: item.modifiedUtc ?? ``,
    }
  }

  return json
}

const NewsDetail: FunctionComponent<GenericPageProps<IEasySoftwareNewsPage>> = (
  props
) => {
  const pageData = props.pageContext.pageData
  const categories = props.pageContext.newsCategories
  const tags = props.pageContext.newsTags
  const author = props.pageContext.newsAuthor
  const headerData = props.pageContext.headerData
  const footerData = props.pageContext.footerData
  const date = new Date(pageData.createdUtc)
  const minutes = getReadingDuration([
    pageData.newsPerex.html,
    pageData.newsContent.html,
  ])

  const widgetData = props.pageContext.widgets?.widgetZone
  const breadcrumbsData = props.pageContext.breadcrumbs
  const editorPath = `widgetZone,widgets`

  const filterOptions = {
    simpleFilterTermIDs: null,
    simpleFilterExplicitIDs: null,
    simpleFilterContentType: `EasySoftwareNews` as RepeaterContentType,
    simpleFilterSortByIDs: false,
    puxFilterIsHidden: true,
  }

  let dateUpdated: Date | null = null

  if (pageData.newsUpdated) {
    dateUpdated = new Date(pageData.newsUpdated)
  }

  return (
    <Layout
      localizedPath={pageData.localization?.localizations}
      footerData={footerData}
      headerData={headerData}
      pageContentItemId={pageData.contentItemId}
      pageProperties={props.pageContext.pageProperties}
      resStrings={props.pageContext.resStrings}
    >
      <div className='NewsDetail'>
        <EasySoftwareBreadcrumbs items={breadcrumbsData} />
        <div className='Container Container-gradient'>
          <PuxContent
            contentModifier='newsDetail'
          >
            {categories?.length && categories.length > 0 ? (
              <div className='NewsDetail-category NewsDetail-parameter'>
                {categories.map((category) => {
                  return (
                    <PuxLink
                      className='NewsCategory-item Button Button--ghost Button--sm'
                      to={`${getLocalizedUrl(category.path)}`}
                    >
                      {category.displayText}
                      {category.newsCategoryIcon ? (
                        <div className='NewsCategory-itemIcon'>
                          <PuxIcon
                            name={category.newsCategoryIcon}
                            size={12}
                            decorative
                          />
                        </div>
                      ) : null}
                    </PuxLink>
                  )
                })}
              </div>
            ) : null}
            <h1 className='NewsDetail-title'>
              <EasySoftwareEditorContainer
                type='input'
                content={pageData.displayText}
                wysiwygId={pageData.contentItemId + `-heading`}
                editorPath={`displayText`}
                pageContentItemId={pageData.contentItemId}
              />
            </h1>

            <div className='NewsDetail-meta'>
              <div className='NewsDetail-date NewsDetail-parameter'>
                {dateUpdated ? (
                  <span>
                    {t`EasyRedmine.News.UpdatedDate` + `: `}
                    <strong>{dateUpdated.toLocaleDateString()}</strong>
                  </span>
                ) : (
                  <strong>{date.toLocaleDateString()}</strong>
                )}
              </div>

              <div className='NewsDetail-time NewsDetail-parameter'>
                {ngettext(
                  msgid`${minutes} minute`,
                  `${minutes} minutes`,
                  minutes
                )}
              </div>

              {!author && pageData.newsAuthor && (
                <div className='NewsDetail-author NewsDetail-parameter'>
                  {pageData.newsAuthor}
                </div>
              )}

              {author && (
                <div className='NewsDetail-puxAuthor NewsDetail-parameter'>
                  <PuxMediaSelector {...author.image} disableLazyload />
                  <PuxLink to={`${getLocalizedUrl(author.url)}`}>
                    {author.name}
                  </PuxLink>
                </div>
              )}
            </div>

            <div className='NewsDetail-perex perex'>
              <EasySoftwareEditorContainer
                wysiwygId={pageData.contentItemId + `-perex`}
                pageContentItemId={pageData.contentItemId}
                content={pageData.newsPerex}
                editorPath={`newsPerex,html`}
              />
            </div>

            <div className='NewsDetail-image'>
              {pageData.puxVideo?.videoYTID.html && (
                <PuxVideo {...pageData.puxVideo} />
              )}
              {!pageData.puxVideo?.videoYTID.html && (
                <PuxMediaSelector {...pageData.newsImage} disableLazyload />
              )}
            </div>

            <div className='NewsDetail-content'>
              <EasySoftwareEditorContainer
                wysiwygId={pageData.contentItemId + `-content`}
                pageContentItemId={pageData.contentItemId}
                content={pageData.newsContent}
                editorPath={`newsContent,html`}
              />
            </div>

            <WidgetBuilder
              widgetBuilderData={widgetData}
              pageContentItemId={pageData.contentItemId}
              editorPath={editorPath}
            />

            {tags?.length && tags.length > 0 ? (
              <div className='NewsDetail-tags'>
                {tags.map((tags) => {
                  return (
                    <PuxLink
                      className='NewsDetail-tagsItem Button Button--ghost Button--md'
                      to={`${getLocalizedUrl(tags.url)}`}
                    >
                      {tags.title}
                    </PuxLink>
                  )
                })}
              </div>
            ) : null}

            {author && author.authorContent && (
              <div className='NewsDetail-authorBio'>
                <div className='NewsDetail-authorBio-header'>
                  <div className='NewsDetail-authorBio-image'>
                    <PuxMediaSelector {...author.image} />
                  </div>
                  <div className='NewsDetail-authorBio-info'>
                    <PuxLink
                      to={`${getLocalizedUrl(author.url)}`}
                      className='NewsDetail-authorBio-name'
                    >
                      {author.name}
                    </PuxLink>

                    <div className='NewsDetail-authorBio-metadata'>
                      {author.authorJob ? (
                        <div className='NewsDetail-authorBio-job'>{author.authorJob}</div>
                      ) : null}
                      {author.authorJob && author.authorLinkedIn ? (
                        <div className='NewsDetail-authorBio-spacer'></div>
                      ) : null}
                      {author.authorLinkedIn ? (
                        <a
                          href={author.authorLinkedIn}
                          target='_blank'
                          className='NewsDetail-authorBio-linkedin PuxSocial-logo PuxSocial-linkedin'
                        >
                          linkedin
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className='NewsDetail-authorBio-content'>
                  <div className='NewsDetail-authorBio-bio' dangerouslySetInnerHTML={{ __html: author.authorContent.html }} />
                </div>
              </div>
            )}
          </PuxContent>
        </div>
      </div>
      {props.pageContext.newsFAQItems?.length &&
        props.pageContext.newsFAQItems?.length > 0 ? (
        <div className='NewsFAQ'>
          <div className='Container'>
            <h2>{t`EasyRedmine.News.FAQItems.Title`}</h2>
            <PuxRepeater
              repeaterData={props.pageContext.newsFAQItems}
              puxSimpleFilter={filterOptions}
              simpleRepeaterPageSize={props.pageContext.newsFAQItems?.length}
              simpleRepeaterPagerOptions='showall'
              simpleRepeaterOnePageOnly
              simpleRepeaterFallbackCulture={{ html: `` }}
            />
          </div>
        </div>
      ) : null}
      {props.pageContext.newsData?.length &&
        props.pageContext.newsData?.length > 0 ? (
        <div className='NewsRelated'>
          <div className='Container'>
            <h2>{t`EasyRedmine.News.RelatedItems.Title`}</h2>
            <PuxRepeater
              repeaterData={props.pageContext.newsData}
              puxSimpleFilter={filterOptions}
              simpleRepeaterPageSize={3}
              simpleRepeaterPagerOptions='showall'
              simpleRepeaterOnePageOnly
              simpleRepeaterFallbackCulture={{ html: `` }}
            />
          </div>
        </div>
      ) : null}
    </Layout>
  )
}

export default NewsDetail

export function Head(
  props: HeadProps<never, IPageContext<IEasySoftwareNewsPage>>
) {
  const pageData = props.pageContext.pageData
  const metaTags = props.pageContext.metaTags

  if (!metaTags) {
    return
  }

  if (!metaTags.metaTagsOGImage?.resizePaths?.length) {
    metaTags.metaTagsOGImage = pageData.newsImage?.mediaSelectorImage
  }
  
  if (!metaTags.metaTagsImage?.resizePaths?.length) {
    metaTags.metaTagsImage = pageData.newsImage?.mediaSelectorImage
  }

  return (
    <>
      <PuxMetaTagsGatsby
        {...metaTags}
        localizedPaths={pageData.localization?.localizations}
        structuredData={[
          getNewsJson(pageData, `BlogPosting`, props.pageContext.newsAuthor),
          getNewsJson(pageData, `NewsArticle`, props.pageContext.newsAuthor),
        ]}
      />
      <PuxVWO isEnabled={props.pageContext.pageProperties?.pagePropertiesVWOEnabled} />
    </>
  )
}
